import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Banner } from '../components/States';
import { InventoryPicker } from '../components/InventoryPicker';
import type { TradeInQuote } from '../api/types';
import { trackSellOrderCreated, trackSellQuote } from '../lib/tracking';
import { abrirAbaEmBranco, enviarAoAtendimento } from '../lib/whatsapp';

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Venda de inventário: o cliente escolhe os itens e recebe a cotação fechada.
 *
 * A cotação é sempre recalculada no servidor no momento de confirmar — o preço
 * de origem e o câmbio mudam, e prometer um valor calculado na tela minutos
 * antes seria assumir um risco que não é do cliente nem nosso.
 */
export function SellPage() {
  const navigate = useNavigate();
  const [fechando, setFechando] = useState(false);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [cotacao, setCotacao] = useState<TradeInQuote | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function fecharCotacao() {
    setOcupado(true);
    setErro(null);
    try {
      const quote = await api.tradeInQuote(selecionados);
      trackSellQuote({ creditoBrl: quote.totalBrl, pecas: selecionados.length });
      setCotacao(quote);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui calcular a cotação.');
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Fecha o pedido de venda e já manda ao atendimento, como a sacola.
   *
   * Antes eram dois passos — fechar aqui, enviar na tela do pedido — e quem
   * parava no primeiro deixava o pedido esquecido como "Não enviado".
   */
  async function fecharPedido() {
    // Aberta ainda dentro do clique: depois de esperar a API o navegador bloqueia.
    const aba = abrirAbaEmBranco();
    setFechando(true);
    setErro(null);
    try {
      const { order } = await api.criarPedido({ kind: 'venda', assetIds: selecionados });
      trackSellOrderCreated(order);
      await enviarAoAtendimento(order, aba);
      navigate(`/pedido/${order.code}`);
    } catch (e) {
      aba?.close();
      setErro(e instanceof ApiError ? e.message : 'Não consegui abrir o pedido.');
    } finally {
      setFechando(false);
    }
  }

  function copiarResumo() {
    if (!cotacao) return;
    const linhas = [
      'Venda de inventário · DMS Skins',
      ...cotacao.items.map((i) => `- ${i.name}: ${brl(i.quoteBrl ?? 0)}`),
      `Total: ${brl(cotacao.totalBrl)} (${cotacao.discountPct}% abaixo do mercado de origem)`,
      `Trade link: ${cotacao.tradeUrl ?? ''}`,
    ];
    void navigator.clipboard.writeText(linhas.join('\n'));
  }

  return (
    <>
      <InventoryPicker
        titulo="Venda de inventário"
        subtitulo="Escolha as skins que você quer vender e veja quanto pagamos por elas, em reais."
        selecionados={selecionados}
        onChange={(ids) => {
          setSelecionados(ids);
          setCotacao(null); // seleção mudou: a cotação anterior não vale mais
        }}
      />

      {erro && <Banner kind="error">{erro}</Banner>}

      {selecionados.length > 0 && !cotacao && (
        <div className="panel">
          <button className="btn primary" onClick={() => void fecharCotacao()} disabled={ocupado}>
            {ocupado ? 'Calculando…' : `Fechar cotação de ${selecionados.length} item(ns)`}
          </button>
        </div>
      )}

      {cotacao && (
        <div className="panel">
          <h2>Sua cotação</h2>

          {cotacao.recusados.length > 0 && (
            <Banner kind="warn">
              {cotacao.recusados.length} item(ns) ficaram de fora:{' '}
              {cotacao.recusados.map((r) => r.motivo).join(' · ')}
            </Banner>
          )}

          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Mercado</th>
                  <th>Pagamos</th>
                </tr>
              </thead>
              <tbody>
                {cotacao.items.map((i) => (
                  <tr key={i.assetId}>
                    <td>{i.name}</td>
                    <td className="dim">{brl(i.refPriceBrl ?? 0)}</td>
                    <td>
                      <b>{brl(i.quoteBrl ?? 0)}</b>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="total-box">
            <span>Total a receber</span>
            <b>{brl(cotacao.totalBrl)}</b>
            <small>{cotacao.discountPct}% abaixo do anúncio mais barato do mercado de origem</small>
          </div>

          <div className="actions-row">
            <button className="btn primary" onClick={() => void fecharPedido()} disabled={fechando}>
              {fechando ? 'Enviando ao atendimento…' : 'Fechar venda e enviar no WhatsApp'}
            </button>
            <button className="btn ghost" onClick={copiarResumo}>
              📋 Copiar resumo
            </button>
            <button className="btn ghost" onClick={() => setCotacao(null)}>
              Refazer seleção
            </button>
          </div>

          <p className="muted">
            A cotação vale enquanto o preço de origem e o câmbio não mudarem. O fechamento é feito pelo atendimento,
            com a oferta enviada para o trade link do seu cadastro.
          </p>
        </div>
      )}
    </>
  );
}
